import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry error tracking for the Web frontend
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE || "development";

  if (!dsn) {
    console.warn("[Sentry] VITE_SENTRY_DSN not configured - error tracking disabled");
    return;
  }

  Sentry.init({
    dsn,
    environment,

    // Performance monitoring
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,

    // Session replay for debugging (sampling rate)
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Release tracking
    release: import.meta.env.VITE_SENTRY_RELEASE,

    // Ignore common errors
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      "chrome-extension://",
      "moz-extension://",
      // Network errors
      "NetworkError",
      "Failed to fetch",
      // Unhandled rejections from user navigation
      "Non-Error promise rejection captured",
    ],

    beforeSend(event, hint) {
      // Filter sensitive data
      if (event.request) {
        // Remove cookie headers
        if (event.request.headers) {
          delete event.request.headers["cookie"];
        }

        // Remove query parameters that might contain tokens
        if (event.request.url) {
          try {
            const url = new URL(event.request.url);
            url.searchParams.delete("token");
            url.searchParams.delete("code");
            url.searchParams.delete("state");
            event.request.url = url.toString();
          } catch (e) {
            // Invalid URL, leave as is
          }
        }
      }

      // Remove sensitive breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.filter((breadcrumb) => {
          // Filter out localStorage breadcrumbs that might contain tokens
          if (breadcrumb.category === "console" && breadcrumb.message?.includes("token")) {
            return false;
          }
          return true;
        });
      }

      return event;
    },
  });

  console.log(`[Sentry] Frontend error tracking initialized (environment: ${environment})`);
}

/**
 * Set user context for error tracking
 */
export function setUserContext(user: { id: string; email?: string; name?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  });
}

/**
 * Clear user context (on logout)
 */
export function clearUserContext() {
  Sentry.setUser(null);
}
